import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Trash2, Edit, Plus, AlertTriangle, CheckCircle, Info, GripVertical } from 'lucide-react';
import { Badge } from '../../../../components/ui/Badge';
import { procedureService } from '../../services/procedureService';
import { maintPlanService } from '../../services/maintPlanService';

// DnD Kit imports
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  defaultDropAnimationSideEffects,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Get safety badge based on content
const getSafetyBadge = (safetyNotes) => {
  if (!safetyNotes) return null;
  const lowerNotes = safetyNotes.toLowerCase();
  if (lowerNotes.includes('danger') || lowerNotes.includes('warning') || lowerNotes.includes('caution')) {
    return { icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-900/20', border: 'border-red-800' };
  }
  if (lowerNotes.includes('note') || lowerNotes.includes('info')) {
    return { icon: Info, color: 'text-blue-400', bg: 'bg-blue-900/20', border: 'border-blue-800' };
  }
  return { icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-900/20', border: 'border-green-800' };
};

// Sortable Procedure Item Component
const SortableProcedureItem = ({
  procedure,
  isEditing,
  onEditStart,
  onEditCancel,
  onEditChange,
  onEditSave,
  onDelete,
  editFormData,
  getSafetyBadge
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: procedure.id, disabled: isEditing });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition, // Remove transition while dragging for instant snap
    opacity: isDragging ? 0.3 : 1, // Make original item semi-transparent while dragging
  };

  const safetyBadge = getSafetyBadge(procedure.safety_notes);
  const SafetyIcon = safetyBadge?.icon;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-lg ${isEditing ? 'border-b border-l' : 'border'} border-gray-600 overflow-hidden ${isDragging ? 'shadow-2xl' : ''
        }`}
    >
      {!isEditing ? (
        <>
          {/* Header row with Title and Duration */}
          <div className="flex justify-between gap-4 p-4 bg-gray-700/50 border-gray-600">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {/* Drag Handle */}
              <div
                {...attributes}
                {...listeners}
                className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-600 rounded transition-colors"
              >
                <GripVertical className="w-4 h-4 text-gray-400" />
              </div>
              <span className="text-sm text-gray-400 font-mono flex-shrink-0">
                #{procedure.step_number}
              </span>
              <h4 className={`text-white font-medium ${!procedure.title.includes(' ') ? 'break-all' : 'break-words'}`}>
                {procedure.title}
              </h4>
            </div>
            <div className="flex gap-2 max-h-7">
              <Badge className="bg-blue-900/50 text-blue-200 whitespace-nowrap">
                {procedure.expected_duration} min
              </Badge>
              <button
                onClick={() => onEditStart(procedure)}
                className="p-1 hover:bg-gray-600 rounded transition-colors"
                title="Edit procedure"
              >
                <Edit className="w-4 h-4 text-gray-400 hover:text-blue-400" />
              </button>
              <button
                onClick={() => onDelete(procedure.id)}
                className="p-1 hover:bg-gray-600 rounded transition-colors"
                title="Delete procedure"
              >
                <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-400" />
              </button>
            </div>
          </div>

          {/* Description */}
          {procedure.description && (
            <div className="p-4 bg-gray-700/30 border-t border-gray-600">
              <p className="text-sm text-gray-400 mb-1">Description</p>
              <p className="text-white text-sm whitespace-pre-wrap break-words">
                {procedure.description}
              </p>
            </div>
          )}

          {/* Safety Notes with badge */}
          {procedure.safety_notes && (
            <div className={`p-4 bg-green-900/20 border-t border-green-800`}>
              <div className="flex items-start gap-2">
                <CheckCircle className={`w-4 h-4 text-green-400 mt-0.5 flex-shrink-0`} />
                <div>
                  <p className={`text-xs text-green-400 mb-1`}>Safety Notes</p>
                  <p className="text-white text-sm whitespace-pre-wrap break-words">
                    {procedure.safety_notes}
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        /* Edit Form */
        <div>
          <div className="flex items-center gap-2 rounded-t-lg px-4 py-2 max-w-[50%] bg-blue-900/50 border-t border-r border-l border-blue-800">
            <p className="text-sm text-blue-300">Editing ...</p>
          </div>
          <div className="space-y-0">
            {/* Header row with Title and Duration */}
            <div className="flex items-center justify-between p-4 bg-gray-700/50 border-b rounded-tr-lg border-t border-r border-gray-600">
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-400 font-mono flex-shrink-0">
                  #{procedure.step_number}
                </span>
                <input
                  type="text"
                  name="title"
                  maxLength={255}
                  value={editFormData.title}
                  onChange={onEditChange}
                  className="text-white overflow-auto font-medium bg-gray-600 rounded-lg pl-2 border-none focus:ring-0"
                />
              </div>
              <div className="flex items-center gap-2 max-h-7">
                <Badge className="bg-blue-900/50 text-blue-200 whitespace-nowrap">
                  <input
                    type="number"
                    name="expected_duration"
                    value={editFormData.expected_duration}
                    min="1"
                    max={Number.MAX_SAFE_INTEGER}
                    onChange={onEditChange}
                    placeholder="00"
                    className="max-w-[30px] rounded-l-lg focus:outline-none focus:ring-0 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  min
                </Badge>
                <button
                  onClick={() => onEditSave(procedure)}
                  className="p-1 text-white hover:bg-gray-600 rounded transition-colors"
                  title="Save changes"
                >
                  ✔
                </button>
                <button
                  onClick={onEditCancel}
                  className="p-1 text-white hover:bg-gray-600 rounded transition-colors"
                  title="Cancel"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Description */}
            <div className="p-4 bg-gray-700/30 border-b border-r border-gray-600">
              <p className="text-sm text-gray-400 mb-1">Description</p>
              <textarea
                name="description"
                value={editFormData.description}
                onChange={onEditChange}
                rows="3"
                placeholder="Enter procedure description..."
                className="w-full text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>

            {/* Safety Notes*/}
            <div className="border-r border-gray-600">
              <div className={`p-4 bg-green-900/20 border-t border-green-800`}>
                <div className="flex items-start gap-2">
                  <CheckCircle className={`w-4 h-4 text-green-400 mt-0.5 flex-shrink-0`} />
                  <div className="w-full">
                    <p className={`text-xs text-green-400 mb-1`}>Safety Notes</p>
                    <textarea
                      name="safety_notes"
                      value={editFormData.safety_notes}
                      onChange={onEditChange}
                      rows="1"
                      placeholder="Enter safety notes..."
                      className="w-full text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-0 focus:ring-yellow-500 resize-none"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Dragging overlay component for smooth visual feedback
const DragOverlayItem = ({ procedure }) => {
  if (!procedure) return null;

  return (
    <div className="rounded-lg border border-blue-500 bg-gray-700 shadow-2xl overflow-hidden opacity-90">
      <div className="flex justify-between gap-4 p-4 bg-gray-700/50">
        <div className="flex items-center gap-3">
          <GripVertical className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-400 font-mono">#{procedure.step_number}</span>
          <h4 className="text-white font-medium">{procedure.title}</h4>
        </div>
        <Badge className="bg-blue-900/50 text-blue-200 whitespace-nowrap">
          {procedure.expected_duration} min
        </Badge>
      </div>
    </div>
  );
};

export const PlansProcedures = ({ planId }) => {
  const queryClient = useQueryClient();

  // Procedures state
  const [newProcedure, setNewProcedure] = useState({
    title: '',
    description: '',
    expected_duration: '',
    safety_notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingProcedureId, setEditingProcedureId] = useState(null);
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    expected_duration: '',
    safety_notes: ''
  });
  const [activeDragId, setActiveDragId] = useState(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Prevents accidental drag on click
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Custom drop animation
  const dropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: {
        active: {
          opacity: '0.5',
        },
      },
    }),
  };

  // Fetch procedures for this plan
  const { data: plans = [], isLoading: proceduresLoading } = useQuery({
    queryKey: ['procedures-and-spare-parts', planId],
    queryFn: () => maintPlanService.getById(planId),
    enabled: !!planId,
  });
  const procedures = plans.procedures || [];
  // Create procedure mutation
  const createProcedureMutation = useMutation({
    mutationFn: (procedureData) => procedureService.create(planId, procedureData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['procedures-and-spare-parts', planId] });
      setNewProcedure({
        title: '',
        description: '',
        expected_duration: '',
        safety_notes: ''
      });
      setIsSubmitting(false);
    },
    onError: (error) => {
      console.error('Error creating procedure:', error);
      setIsSubmitting(false);
    },
  });

  // Update procedure mutation
  const updateProcedureMutation = useMutation({
    mutationFn: ({ id, data }) => procedureService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['procedures-and-spare-parts', planId] });
      setEditingProcedureId(null);
      setEditFormData({
        title: '',
        description: '',
        expected_duration: '',
        safety_notes: ''
      });
    },
    onError: (error) => {
      console.error('Error updating procedure:', error);
    },
  });

  // Delete procedure mutation
  const deleteProcedureMutation = useMutation({
    mutationFn: (procedureId) => procedureService.delete(procedureId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['procedures-and-spare-parts', planId] });
    },
  });

  // Sort procedures by step number
  const sortedProcedures = [...procedures].sort((a, b) => a.step_number - b.step_number);

  // Handle drag end - INSTANT visual update
  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveDragId(null);

    if (!over || active.id === over.id) return;
    if (editingProcedureId) return;

    const oldIndex = sortedProcedures.findIndex((item) => item.id === active.id);
    const newIndex = sortedProcedures.findIndex((item) => item.id === over.id);

    // Reorder IMMEDIATELY in local state (instant visual update)
    const reordered = arrayMove(sortedProcedures, oldIndex, newIndex);

    // Update step numbers in reordered array
    const updatedReorderedProcedures = reordered.map((proc, idx) => ({
      ...proc,
      step_number: idx + 1
    }));

    // Get the current cached data
    const currentCachedData = queryClient.getQueryData(['procedures-and-spare-parts', planId]);

    // If we have cached data, preserve its structure
    if (currentCachedData) {
      const updatedCacheData = {
        ...currentCachedData,
        procedures: updatedReorderedProcedures
      };
      queryClient.setQueryData(['procedures-and-spare-parts', planId], updatedCacheData);
    } else {
      // Fallback: just set the procedures array (less ideal but prevents crash)
      queryClient.setQueryData(['procedures-and-spare-parts', planId], updatedReorderedProcedures);
    }

    // Then update backend in background (silent update)
    const updates = updatedReorderedProcedures.map((proc) => ({
      id: proc.id,
      step_number: proc.step_number
    }));

    // Fire and forget - don't wait for response to update UI
    Promise.all(updates.map(update =>
      procedureService.update(update.id, { step_number: update.step_number })
    )).catch(error => {
      console.error('Error reordering procedures:', error);
      // If backend fails, revert to original order
      queryClient.invalidateQueries({ queryKey: ['procedures-and-spare-parts', planId] });
    });
  };

  const handleDragStart = (event) => {
    setActiveDragId(event.active.id);
  };

  const handleAddProcedure = async () => {
    if (!newProcedure.title.trim()) {
      return;
    }

    setIsSubmitting(true);
    createProcedureMutation.mutate({
      title: newProcedure.title.trim(),
      description: newProcedure.description.trim(),
      expected_duration: parseInt(newProcedure.expected_duration),
      safety_notes: newProcedure.safety_notes.trim() || null,
      step_number: sortedProcedures.length + 1
    });
  };

  const handleDeleteProcedure = (procedureId) => {
    if (window.confirm('Are you sure you want to delete this procedure?')) {
      deleteProcedureMutation.mutate(procedureId);
    }
  };

  const handleEditStart = (procedure) => {
    setEditingProcedureId(procedure.id);
    setEditFormData({
      title: procedure.title,
      description: procedure.description || '',
      expected_duration: procedure.expected_duration,
      safety_notes: procedure.safety_notes || ''
    });
  };

  const handleEditCancel = () => {
    setEditingProcedureId(null);
    setEditFormData({
      title: '',
      description: '',
      expected_duration: '',
      safety_notes: ''
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: name === 'title' ? value.slice(0, 255) : value
    }));
  };

  const handleEditSave = (procedure) => {
    if (!editFormData.title.trim()) {
      return;
    }

    updateProcedureMutation.mutate({
      id: procedure.id,
      data: {
        title: editFormData.title.trim(),
        description: editFormData.description.trim(),
        expected_duration: parseInt(editFormData.expected_duration),
        safety_notes: editFormData.safety_notes.trim() || null
      }
    });
  };

  const handleProcedureInputChange = (e) => {
    const { name, value } = e.target;
    setNewProcedure(prev => ({
      ...prev,
      [name]: name === 'title' ? value.slice(0, 255) : value
    }));
  };

  const activeProcedure = activeDragId ? sortedProcedures.find(p => p.id === activeDragId) : null;

  if (proceduresLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Existing Procedures */}
      {sortedProcedures.length > 0 ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Existing Procedures</h3>
          </div>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            dropAnimation={dropAnimation}
          >
            <SortableContext
              items={sortedProcedures.map(p => p.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-4">
                {sortedProcedures.map((procedure) => (
                  <SortableProcedureItem
                    key={procedure.id}
                    procedure={procedure}
                    isEditing={editingProcedureId === procedure.id}
                    onEditStart={handleEditStart}
                    onEditCancel={handleEditCancel}
                    onEditChange={handleEditChange}
                    onEditSave={handleEditSave}
                    onDelete={handleDeleteProcedure}
                    editFormData={editFormData}
                    getSafetyBadge={getSafetyBadge}
                  />
                ))}
              </div>
            </SortableContext>

            <DragOverlay dropAnimation={dropAnimation}>
              {activeProcedure && (
                <DragOverlayItem procedure={activeProcedure} />
              )}
            </DragOverlay>
          </DndContext>
        </div>
      ) : (
        <div className="text-center text-gray-400 py-8">
          <p>No procedures added yet</p>
        </div>
      )}

      {/* Add New Procedure Form */}
      <div className="border-t border-gray-700 pt-6 mt-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Plus className="w-5 h-5 text-blue-400" />
          Add New Procedure
        </h3>

        <div className="space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={newProcedure.title}
              onChange={handleProcedureInputChange}
              placeholder="Enter procedure title"
              className="w-full px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isSubmitting}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={newProcedure.description}
              onChange={handleProcedureInputChange}
              rows="3"
              placeholder="Enter procedure description..."
              className="w-full px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              disabled={isSubmitting}
            />
          </div>

          {/* Expected Duration and Safety Notes row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Expected Duration <span className="text-red-400">*</span>
              </label>
              <div className="flex gap-2 items-center">
                <input
                  type="number"
                  name="expected_duration"
                  value={newProcedure.expected_duration}
                  onChange={handleProcedureInputChange}
                  min="1"
                  max={Number.MAX_SAFE_INTEGER}
                  placeholder="Duration in minutes"
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isSubmitting}
                />
                <span className="text-gray-400">minutes</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Safety Notes
              </label>
              <textarea
                name="safety_notes"
                value={newProcedure.safety_notes}
                onChange={handleProcedureInputChange}
                rows="2"
                placeholder="Enter safety notes (optional)"
                className="w-full px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleAddProcedure}
              disabled={isSubmitting || !newProcedure.title.trim() || !newProcedure.expected_duration}
              className="inline-flex items-center gap-2 px-6 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Add Procedure
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};